/**
 * Browser-native Speech-to-Text + Text-to-Speech for the AI Coach.
 *
 * Uses the standard Web Speech API (window.SpeechRecognition,
 * window.speechSynthesis). Works offline in Chromium-based browsers
 * (Chrome, Edge, Opera, Brave) and most modern Safari builds.
 *
 * If the browser does not support SpeechRecognition (e.g. Firefox), the
 * server-side /api/coach/voice/transcribe endpoint (OpenAI Whisper) is the
 * fallback for the mobile shell.
 */

export function isSpeechRecognitionSupported() {
  return typeof window !== "undefined" && (
    "SpeechRecognition" in window || "webkitSpeechRecognition" in window
  );
}

export function isSpeechSynthesisSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function createRecognizer({ onResult, onError, onEnd, lang = "en-US" } = {}) {
  if (!isSpeechRecognitionSupported()) return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const rec = new SR();
  rec.lang = lang;
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;

  rec.onresult = (e) => {
    let final = "";
    let interim = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) final += t;
      else interim += t;
    }
    onResult && onResult({ final, interim });
  };
  rec.onerror = (e) => onError && onError(e);
  rec.onend = () => onEnd && onEnd();
  return rec;
}

let _voiceCache = null;
function _pickCoachVoice() {
  if (!isSpeechSynthesisSupported()) return null;
  if (_voiceCache) return _voiceCache;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;
  // Prefer a warm, neutral English voice
  const prefer = ["Samantha", "Karen", "Google US English", "Microsoft Aria", "Microsoft Jenny", "en-US"];
  for (const p of prefer) {
    const v = voices.find((v) => v.name?.includes(p) || v.lang === p);
    if (v) { _voiceCache = v; return v; }
  }
  _voiceCache = voices.find((v) => v.lang?.startsWith("en")) || voices[0];
  return _voiceCache;
}

export function speak(text, { rate = 1, pitch = 1, onStart, onEnd } = {}) {
  if (!isSpeechSynthesisSupported() || !text) return null;
  // cancel anything still queued so the latest answer wins
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const v = _pickCoachVoice();
  if (v) u.voice = v;
  u.rate = rate;
  u.pitch = pitch;
  u.onstart = onStart || null;
  u.onend = onEnd || null;
  window.speechSynthesis.speak(u);
  return u;
}

export function stopSpeaking() {
  if (isSpeechSynthesisSupported()) window.speechSynthesis.cancel();
  if (_currentAudio) {
    try { _currentAudio.pause(); _currentAudio.src = ""; } catch (e) {}
    _currentAudio = null;
  }
}

/* ---------- Persona TTS (OpenAI voices via /api/coach/tts) ---------- */
let _currentAudio = null;

/**
 * Stream-and-play the user's coach-persona voice through `<audio>` element using
 * the server-side /api/coach/tts endpoint. Returns the HTMLAudioElement so the
 * caller can attach further listeners if needed. Falls back to Web Speech API
 * `speak()` on any failure.
 */
export async function speakWithPersona(text, { onStart, onEnd, apiBase } = {}) {
  if (!text) return null;
  stopSpeaking();
  const base = apiBase || (typeof window !== "undefined" ? (window.__API_BASE__ || "") : "");
  try {
    const token = localStorage.getItem("ps_token");
    const res = await fetch(`${base}/api/coach/tts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error(`tts ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    _currentAudio = audio;
    audio.addEventListener("playing", () => onStart && onStart());
    audio.addEventListener("ended", () => {
      URL.revokeObjectURL(url);
      _currentAudio = null;
      onEnd && onEnd();
    });
    audio.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      _currentAudio = null;
      onEnd && onEnd();
    });
    try {
      await audio.play();
    } catch (err) {
      // Autoplay blocked or other error — clean up and call onEnd
      URL.revokeObjectURL(url);
      _currentAudio = null;
      onEnd && onEnd();
      return null;
    }
    return audio;
  } catch (e) {
    // Fallback to browser TTS
    return speak(text, { onStart, onEnd });
  }
}
